import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fmtDateTimeCST } from '../utils/datetime';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
  ArrowUturnLeftIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  PlusIcon,
  ShieldCheckIcon,
  SparklesIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { documentApi, projectApi, tenderApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { hasFeature } from '../utils/license';
import type { ExportTemplate } from '../services/api';
import type { OutlineItem, TrashItem } from '../types';
import VersionManager from '../components/VersionManager';
import ShareDialog from '../components/ShareDialog';
import MarkdownRenderer from '../components/MarkdownRenderer';
import {
  CLAUSE_CATEGORY_LABELS,
  COMPLIANCE_CATEGORY_LABELS,
  COMPLIANCE_OVERALL_LABELS,
  COMPLIANCE_SEVERITY_LABELS,
  COMPLIANCE_STATUS_CLASSES,
  COMPLIANCE_STATUS_LABELS,
  INDUSTRY_LABELS,
  PART_KIND_LABELS,
  PRICE_TYPE_LABELS,
  PURCHASE_METHOD_LABELS,
  type TenderClause,
  type TenderClauseCategory,
  type TenderComplianceReport,
  type TenderComplianceRule,
  type TenderComplianceSeverity,
  type TenderIndustry,
  type TenderOutlineNode,
  type TenderPriceReference,
  type TenderPriceType,
  type TenderProject,
  type TenderProjectDetail,
  type TenderPurchaseMethod,
  type TenderTemplate,
} from '../types/tender';
import TenderProjectForm, {
  buildInfoPayload,
  emptyTenderForm,
  formFromInfo,
  type TenderFormState,
} from '../pages/tender/TenderProjectForm';
import TenderOutlinePanel from '../pages/tender/TenderOutlinePanel';
import TenderOutlineEditor, {
  type TenderOutlineEditPayload,
} from '../pages/tender/TenderOutlineEditor';

/** 标签文本 → 数组（支持 、 逗号 空格 分隔，去空去重） */
const parseClauseTags = (text: string): string[] => {
  const seen = new Set<string>();
  return text
    .split(/[、,，\s]+/)
    .map((item) => item.trim())
    .filter((item) => {
      if (!item || seen.has(item)) {
        return false;
      }
      seen.add(item);
      return true;
    });
};

/** 价格参考：常用品类（下拉候选，也允许直接输入新值） */
const PRICE_CATEGORY_OPTIONS = [
  '土石方',
  '道路',
  '桥涵',
  '给排水',
  '电气照明',
  '绿化',
  '交通设施',
  '建筑工程',
  '安装工程',
  '装饰装修',
  '材料',
  '设备',
  '人工',
  '机械',
  '其他',
];

/** 价格参考：常用计价单位（下拉候选，也允许直接输入新值） */
const PRICE_UNIT_OPTIONS = [
  '元',
  '元/m',
  '元/㎡',
  '元/m³',
  '元/km',
  '元/座',
  '元/套',
  '元/台',
  '元/个',
  '元/吨',
  '元/kg',
  '元/项',
  '元/批',
  '元/次',
  '元/人·月',
  '元/人·天',
  '元/月',
  '元/年',
];

interface TenderDocWorkspaceProps {
  onBack: () => void;
  onLogout: () => void;
}

type WorkspaceTab = 'info' | 'content' | 'library' | 'compliance' | 'export';

const TABS: Array<{ key: WorkspaceTab; label: string }> = [
  { key: 'info', label: '立项信息' },
  { key: 'content', label: '目录与正文' },
  { key: 'library', label: '资料库' },
  { key: 'compliance', label: '合规审核' },
  { key: 'export', label: '导出' },
];

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

/**
 * 招标文件编写工作台（招标方视角）
 *
 * 流程：选模板 + 立项信息 → 目录实例化 → 逐章编制（SSE）→ 导出 Word。
 * 项目容器复用 project 表（doc_kind=tender），目录与正文复用 outline_node / chapter_content。
 */
export default function TenderDocWorkspace({ onBack, onLogout }: TenderDocWorkspaceProps) {
  const { license } = useAuth();
  const [projects, setProjects] = useState<TenderProject[]>([]);
  const [templates, setTemplates] = useState<TenderTemplate[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [detail, setDetail] = useState<TenderProjectDetail | null>(null);
  const [tab, setTab] = useState<WorkspaceTab>('info');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<TenderFormState>(emptyTenderForm);
  const [creating, setCreating] = useState(false);
  const [editForm, setEditForm] = useState<TenderFormState>(emptyTenderForm);
  const [savingInfo, setSavingInfo] = useState(false);

  const [selectedStableId, setSelectedStableId] = useState<string | null>(null);
  /** stable_id → 正文（含未保存的本地编辑） */
  const [contents, setContents] = useState<Record<string, string>>({});
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [savingContent, setSavingContent] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  /** 正文区视图：预览（Markdown 渲染）/ 编辑（源码） */
  const [contentView, setContentView] = useState<'preview' | 'edit'>('preview');
  /** 批量编制（编制所有章节）运行状态与中断控制器 */
  const [batchRunning, setBatchRunning] = useState(false);
  const batchAbortRef = useRef<AbortController | null>(null);

  // 资料库：条款库 + 价格参考
  const [clauses, setClauses] = useState<TenderClause[]>([]);
  const [clauseFilter, setClauseFilter] = useState<TenderClauseCategory | ''>('');
  const [clauseForm, setClauseForm] = useState<{
    title: string;
    content: string;
    category: TenderClauseCategory;
    purchase_methods: TenderPurchaseMethod[];
    industry: TenderIndustry | '';
    legal_basis: string;
    tagsText: string;
    is_active: boolean;
  }>({
    title: '',
    content: '',
    category: 'qualification',
    purchase_methods: [],
    industry: '',
    legal_basis: '',
    tagsText: '',
    is_active: true,
  });
  const [savingClause, setSavingClause] = useState(false);
  // 正在编辑的条款 id（null 表示新增）：条款库支持改标题/类别/内容
  const [editingClauseId, setEditingClauseId] = useState<number | null>(null);

  const [priceRefs, setPriceRefs] = useState<TenderPriceReference[]>([]);
  // 正在编辑的价格参考 id（null 表示新增）
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [priceForm, setPriceForm] = useState<{
    name: string;
    category: string;
    region: string;
    unit: string;
    price: string;
    price_type: TenderPriceType;
    reference_date: string;
  }>({
    name: '',
    category: '',
    region: '',
    unit: '',
    price: '',
    price_type: 'market',
    reference_date: '',
  });
  const [savingPrice, setSavingPrice] = useState(false);

  // 合规审核
  const [complianceReport, setComplianceReport] = useState<TenderComplianceReport | null>(null);
  const [complianceRules, setComplianceRules] = useState<TenderComplianceRule[]>([]);
  const [complianceRunning, setComplianceRunning] = useState(false);
  const [complianceLogs, setComplianceLogs] = useState<string[]>([]);
  const [ruleForm, setRuleForm] = useState<{
    title: string;
    check_prompt: string;
    severity: TenderComplianceSeverity;
  }>({ title: '', check_prompt: '', severity: 'should' });
  const [savingRule, setSavingRule] = useState(false);
  // 正在编辑的合规检查项 id（null 表示新增）
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);

  // 版本与协作
  const [showVersions, setShowVersions] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // 回收站
  const [showTrash, setShowTrash] = useState(false);
  const [trashItems, setTrashItems] = useState<TrashItem[]>([]);

  // 目录编辑
  const [showOutlineEditor, setShowOutlineEditor] = useState(false);
  const [savingOutline, setSavingOutline] = useState(false);

  // 重新生成目录（按模板补齐 / 重建）
  const [regeneratingOutline, setRegeneratingOutline] = useState(false);
  const [showRegenMenu, setShowRegenMenu] = useState(false);

  // 未保存的正文编辑（按 stable_id 记录）：编制/重新生成前提示，避免静默覆盖人工修改
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());

  const [exportTemplates, setExportTemplates] = useState<ExportTemplate[]>([]);
  const [exportTemplateId, setExportTemplateId] = useState<string>('');
  const [exportTaskId, setExportTaskId] = useState<number | null>(null);
  const [exportStatus, setExportStatus] = useState<string>('');
  const [exportFileName, setExportFileName] = useState<string>('');
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const selectedNode = useMemo(
    () => detail?.outline.find((node) => node.stable_id === selectedStableId) || null,
    [detail, selectedStableId],
  );

  /** 品类下拉候选 = 预置选项 + 库中已用过的品类（便于沿用历史取值） */
  const priceCategoryOptions = useMemo(() => {
    const values = new Set(PRICE_CATEGORY_OPTIONS);
    priceRefs.forEach((item) => {
      if (item.category) {
        values.add(item.category);
      }
    });
    return Array.from(values);
  }, [priceRefs]);

  /** 单位下拉候选 = 预置选项 + 库中已用过的单位 */
  const priceUnitOptions = useMemo(() => {
    const values = new Set(PRICE_UNIT_OPTIONS);
    priceRefs.forEach((item) => {
      if (item.unit) {
        values.add(item.unit);
      }
    });
    return Array.from(values);
  }, [priceRefs]);

  /** 尚无正文内容的章节数（批量编制默认只补这些，避免覆盖人工修改） */
  const pendingCount = useMemo(
    () =>
      (detail?.outline || []).filter((node) => !(contents[node.stable_id] || '').trim()).length,
    [detail, contents],
  );

  const loadProjects = useCallback(async () => {
    try {
      const res = await tenderApi.listProjects();
      setProjects(res.data.items || []);
    } catch (err) {
      setError(getErrorMessage(err, '加载招标文件项目失败'));
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await tenderApi.listTemplates();
      setTemplates(res.data.items || []);
      setCreateForm((prev) =>
        prev.template_id ? prev : { ...prev, template_id: res.data.items?.[0]?.id ?? null },
      );
    } catch (err) {
      setError(getErrorMessage(err, '加载模板失败'));
    }
  }, []);

  const loadDetail = useCallback(async (projectId: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenderApi.getProject(projectId);
      const data = res.data.data;
      setDetail(data);
      setContents(data.contents || {});
      setDirtyIds(new Set());
      setEditForm(formFromInfo(data.project.name, data.info));
      setSelectedStableId((prev) => {
        if (prev && data.outline.some((node) => node.stable_id === prev)) {
          return prev;
        }
        return data.outline[0]?.stable_id ?? null;
      });
    } catch (err) {
      setError(getErrorMessage(err, '加载项目详情失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProjects();
    void loadTemplates();
  }, [loadProjects, loadTemplates]);

  useEffect(() => {
    void documentApi
      .getExportTemplates()
      .then((res) => {
        const list = res.data.templates || [];
        setExportTemplates(list);
        setExportTemplateId((prev) => prev || list[0]?.id || '');
      })
      .catch(() => {
        // 导出模板拉取失败不阻塞主流程
      });
  }, []);

  useEffect(() => {
    if (activeId === null) {
      setDetail(null);
      return;
    }
    void loadDetail(activeId);
  }, [activeId, loadDetail]);

  // 导出任务轮询
  useEffect(() => {
    if (!exportTaskId) {
      return;
    }
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const res = await documentApi.getExportStatus(exportTaskId);
        if (cancelled) {
          return;
        }
        setExportStatus(res.data.status);
        if (res.data.filename) {
          setExportFileName(res.data.filename);
        }
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          window.clearInterval(timer);
          setExporting(false);
          if (res.data.status === 'failed') {
            setExportError(res.data.error_message || '导出失败');
          }
        }
      } catch {
        // 轮询失败下一轮重试
      }
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [exportTaskId]);

  const handleCreate = async () => {
    if (!createForm.template_id) {
      setError('请先选择模板');
      return;
    }
    setCreating(true);
    setError(null);
    setNotice(null);
    try {
      const res = await tenderApi.createProject({
        name: createForm.name.trim(),
        template_id: createForm.template_id,
        info: buildInfoPayload(createForm),
      });
      setNotice(res.data.message);
      setShowCreate(false);
      setCreateForm(emptyTenderForm());
      await loadProjects();
      setActiveId(res.data.data.project.id);
      setTab('content');
    } catch (err) {
      setError(getErrorMessage(err, '创建招标文件项目失败'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (project: TenderProject) => {
    if (
      !window.confirm(
        `确定删除招标文件项目「${project.name}」吗？\n可在「我的设置 → 回收站」中恢复或彻底删除。`,
      )
    ) {
      return;
    }
    setError(null);
    setNotice(null);
    try {
      await projectApi.delete(project.id);
      if (activeId === project.id) {
        setActiveId(null);
        setDetail(null);
        setShowCreate(false);
      }
      await loadProjects();
      setNotice(`项目「${project.name}」已移至回收站`);
    } catch (err) {
      setError(getErrorMessage(err, '删除项目失败'));
    }
  };

  const loadTrash = useCallback(async () => {
    try {
      const res = await tenderApi.listTrash();
      setTrashItems(res.data.trash_items || []);
    } catch (err) {
      setError(getErrorMessage(err, '加载回收站失败'));
    }
  }, []);

  const handleRestoreProject = async (projectId: number, projectName: string) => {
    setError(null);
    setNotice(null);
    try {
      await tenderApi.restore(projectId);
      await Promise.all([loadTrash(), loadProjects()]);
      setNotice(`项目「${projectName}」已恢复`);
    } catch (err) {
      setError(getErrorMessage(err, '恢复项目失败'));
    }
  };

  const handleSaveInfo = async () => {
    if (!detail) {
      return;
    }
    setSavingInfo(true);
    setError(null);
    setNotice(null);
    try {
      const res = await tenderApi.updateInfo(detail.project.id, buildInfoPayload(editForm));
      setNotice(res.data.message);
      setDetail({ ...detail, info: res.data.data });
    } catch (err) {
      setError(getErrorMessage(err, '保存立项信息失败'));
    } finally {
      setSavingInfo(false);
    }
  };

  const handleGenerate = async (node: TenderOutlineNode) => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setError('未授权「AI 生成」功能，请联系管理员');
      return;
    }
    if (!detail) {
      return;
    }
    if (dirtyIds.has(node.stable_id)) {
      const proceed = window.confirm(
        `「${node.title}」有未保存的手工修改，编制会覆盖这些修改。是否继续？（建议先点「保存本章」）`,
      );
      if (!proceed) {
        return;
      }
    }
    setGeneratingId(node.stable_id);
    setError(null);
    setNotice(null);
    setLogs([`正在编制「${node.title}」…`]);
    let buffer = '';
    try {
      await tenderApi.generatePartStream(detail.project.id, node.stable_id, (event) => {
        if (event.error) {
          throw new Error(event.message || '章节编制失败');
        }
        if (event.type === 'progress' && event.message) {
          setLogs((prev) => [...prev, event.message as string]);
        }
        if (event.chunk) {
          buffer += event.chunk;
          setContents((prev) => ({ ...prev, [node.stable_id]: buffer }));
        }
      });
      setDirtyIds((prev) => {
        const next = new Set(prev);
        next.delete(node.stable_id);
        return next;
      });
      setLogs((prev) => [...prev, '编制完成，请检查后点击「保存本章」']);
      setNotice('章节已生成（尚未保存），确认内容后点击「保存本章」');
    } catch (err) {
      setError(getErrorMessage(err, '章节编制失败，请稍后重试'));
      setLogs((prev) => [...prev, getErrorMessage(err, '章节编制失败')]);
    } finally {
      setGeneratingId(null);
    }
  };

  /** 已有正文的章节标识（用于目录编辑时提示删除风险） */
  const filledStableIds = useMemo(
    () =>
      new Set(
        Object.entries(contents)
          .filter(([, value]) => (value || '').trim())
          .map(([key]) => key),
      ),
    [contents],
  );

  /** 保存目录（增删改 / 层级与顺序调整，正文按 stable_id 保留） */
  const handleSaveOutline = async (payload: TenderOutlineEditPayload[]) => {
    if (!detail) {
      return;
    }
    setSavingOutline(true);
    setError(null);
    setNotice(null);
    try {
      const res = await tenderApi.updateOutline(
        detail.project.id,
        payload as unknown as Array<{
          title: string;
          description?: string | null;
          word_count_range?: string | null;
          part_kind?: string | null;
          section?: string | null;
          stable_id?: string | null;
          children?: unknown[];
        }>,
      );
      setNotice(res.data.message);
      setShowOutlineEditor(false);
      await loadDetail(detail.project.id);
    } catch (err) {
      setError(getErrorMessage(err, '保存目录失败'));
    } finally {
      setSavingOutline(false);
    }
  };

  /** 按当前模板同步目录章节标题与分部（已生成正文按 stable_id 保留） */
  const handleSyncOutline = async () => {
    if (!detail) {
      return;
    }
    if (
      !window.confirm(
        '按当前模板同步目录？\n只更新章节标题与分部，不新增/删除章节，已生成的正文保留。',
      )
    ) {
      return;
    }
    setError(null);
    setNotice(null);
    try {
      const res = await tenderApi.syncOutline(detail.project.id);
      setNotice(res.data.message);
      await loadDetail(detail.project.id);
    } catch (err) {
      setError(getErrorMessage(err, '同步目录失败'));
    }
  };

  /** 按模板重新生成目录：merge=补齐缺失章节；replace=按模板重建（正文按标题匹配保留） */
  const handleRegenerateOutline = async (mode: 'merge' | 'replace') => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setError('未授权「AI 生成」功能，请联系管理员');
      return;
    }
    if (!detail) {
      return;
    }
    setShowRegenMenu(false);
    const templateName = detail.info?.template_name || '当前模板';
    const confirmed = window.confirm(
      mode === 'merge'
        ? `按模板「${templateName}」补齐缺失章节？\n现有章节、人工增补章节与已生成正文都会保留。`
        : `按模板「${templateName}」重建整棵目录？\n模板之外的章节会被移除，正文按章节标题匹配保留（标题不同则不再关联，可在版本记录中找回）。`,
    );
    if (!confirmed) {
      return;
    }
    setRegeneratingOutline(true);
    setError(null);
    setNotice(null);
    try {
      const res = await tenderApi.regenerateOutline(detail.project.id, { mode });
      setNotice(res.data.message);
      await loadDetail(detail.project.id);
    } catch (err) {
      setError(getErrorMessage(err, '重新生成目录失败'));
    } finally {
      setRegeneratingOutline(false);
    }
  };

  /** 停止批量编制（已完成章节已由后端落库） */
  const handleStopBatch = () => {
    batchAbortRef.current?.abort();
  };

  /** 编制所有章节：默认只补未编制章节，全部已编制时二次确认后全部重编 */
  const handleGenerateAll = async () => {
    if (!hasFeature(license?.features, 'ai_generate')) {
      setError('未授权「AI 生成」功能，请联系管理员');
      return;
    }
    if (!detail || batchRunning) {
      return;
    }
    if (dirtyIds.size > 0) {
      const proceed = window.confirm(
        `有 ${dirtyIds.size} 个章节存在未保存的手工修改，批量编制会覆盖这些修改。是否继续？（建议先保存）`,
      );
      if (!proceed) {
        return;
      }
    }
    const pending = detail.outline.filter(
      (node) => !(contents[node.stable_id] || '').trim(),
    );
    const skipExisting = pending.length > 0;
    if (!skipExisting && detail.outline.length > 0) {
      const confirmed = window.confirm(
        `全部 ${detail.outline.length} 个章节都已有内容，确定全部重新编制吗？\n` +
          '现有正文会被模型重写（建议先用「版本」保存一次快照）。',
      );
      if (!confirmed) {
        return;
      }
    }
    const targetCount = skipExisting ? pending.length : detail.outline.length;
    if (targetCount === 0) {
      setNotice('没有需要编制的章节');
      return;
    }

    setBatchRunning(true);
    setError(null);
    setNotice(null);
    setLogs([
      skipExisting
        ? `开始批量编制：${targetCount} 个未编制章节（已有内容的章节会跳过）…`
        : `开始批量重编：共 ${targetCount} 个章节…`,
    ]);
    const controller = new AbortController();
    batchAbortRef.current = controller;
    try {
      await tenderApi.generateAllStream(
        detail.project.id,
        { skip_existing: skipExisting },
        (event) => {
          if (event.error) {
            throw new Error(event.message || '批量编制失败');
          }
          if (event.type === 'progress' && event.message) {
            setLogs((prev) => [...prev, event.message as string]);
          }
          if (event.type === 'result' && event.stable_id) {
            setContents((prev) => ({
              ...prev,
              [event.stable_id as string]: event.content || '',
            }));
            setDirtyIds((prev) => {
              const next = new Set(prev);
              next.delete(event.stable_id as string);
              return next;
            });
            setSelectedStableId(event.stable_id as string);
            setLogs((prev) => [
              ...prev,
              `[${event.index}/${event.total}] 「${event.title}」已完成并保存`,
            ]);
          }
          if (event.type === 'result' && event.finished) {
            setLogs((prev) => [
              ...prev,
              `批量编制结束：本次完成 ${event.generated ?? 0} 个章节`,
            ]);
          }
        },
        controller.signal,
      );
      setNotice('批量编制完成，可在目录中逐章检查');
      await loadDetail(detail.project.id);
    } catch (err) {
      const message = getErrorMessage(err, '批量编制失败');
      if (controller.signal.aborted) {
        setNotice('已停止批量编制；已完成的章节已保存，可再次点击继续');
      } else {
        setError(message);
      }
      setLogs((prev) => [...prev, message]);
    } finally {
      setBatchRunning(false);
      batchAbortRef.current = null;
    }
  };

  const handleSaveContent = async () => {
    if (!detail || !selectedNode) {
      return;
    }
    setSavingContent(true);
    setError(null);
    try {
      await projectApi.saveChapterContent(
        detail.project.id,
        selectedNode.node_id,
        contents[selectedNode.stable_id] || '',
        selectedNode.section,
        selectedNode.stable_id,
      );
      setDirtyIds((prev) => {
        const next = new Set(prev);
        next.delete(selectedNode.stable_id);
        return next;
      });
      setNotice(`「${selectedNode.title}」已保存`);
    } catch (err) {
      setError(getErrorMessage(err, '保存章节失败'));
    } finally {
      setSavingContent(false);
    }
  };

  const loadLibrary = useCallback(async () => {
    try {
      const [clauseRes, priceRes] = await Promise.all([
        tenderApi.listClauses(clauseFilter ? { category: clauseFilter } : undefined),
        tenderApi.listPriceReferences(),
      ]);
      setClauses(clauseRes.data.items || []);
      setPriceRefs(priceRes.data.items || []);
    } catch (err) {
      setError(getErrorMessage(err, '加载资料库失败'));
    }
  }, [clauseFilter]);

  useEffect(() => {
    if (tab !== 'library') {
      return;
    }
    void loadLibrary();
  }, [tab, loadLibrary]);

  /** 复位条款表单（保留当前类别，新增/编辑/取消共用） */
  const resetClauseForm = (category: TenderClauseCategory) => {
    setClauseForm({
      title: '',
      content: '',
      category,
      purchase_methods: [],
      industry: '',
      legal_basis: '',
      tagsText: '',
      is_active: true,
    });
  };

  const handleSubmitClause = async () => {
    if (!clauseForm.title.trim() || !clauseForm.content.trim()) {
      setError('条款标题与内容不能为空');
      return;
    }
    setSavingClause(true);
    setError(null);
    setNotice(null);
    try {
      const payload = {
        title: clauseForm.title.trim(),
        content: clauseForm.content.trim(),
        category: clauseForm.category,
        // 空数组/空串表示「通用」或「清空」，需一并提交才能覆盖旧值
        purchase_methods: clauseForm.purchase_methods,
        industry: clauseForm.industry,
        legal_basis: clauseForm.legal_basis.trim(),
        tags: parseClauseTags(clauseForm.tagsText),
        is_active: clauseForm.is_active,
      };
      if (editingClauseId !== null) {
        await tenderApi.updateClause(editingClauseId, payload);
        setNotice('条款已更新，编制相关分部时会引用新内容');
      } else {
        await tenderApi.createClause(payload);
        setNotice('条款已加入资料库，编制相关分部时会自动引用');
      }
      resetClauseForm(clauseForm.category);
      setEditingClauseId(null);
      await loadLibrary();
    } catch (err) {
      setError(
        getErrorMessage(err, editingClauseId !== null ? '更新条款失败' : '新增条款失败'),
      );
    } finally {
      setSavingClause(false);
    }
  };

  /** 载入某条自有条款到表单进行编辑（内置条款只读，不提供编辑入口） */
  const handleEditClause = (clause: TenderClause) => {
    setEditingClauseId(clause.id);
    setClauseForm({
      title: clause.title,
      content: clause.content,
      category: clause.category,
      purchase_methods: clause.purchase_methods || [],
      industry: clause.industry || '',
      legal_basis: clause.legal_basis || '',
      tagsText: (clause.tags || []).join('、'),
      is_active: clause.is_active !== false,
    });
    setError(null);
    setNotice('正在编辑条款，点「保存修改」生效，或点「取消编辑」放弃');
  };

  const handleCancelClauseEdit = () => {
    setEditingClauseId(null);
    resetClauseForm(clauseForm.category);
  };

  const handleDeleteClause = async (clauseId: number) => {
    if (!window.confirm('确定删除该条款吗？删除后编制章节将不再引用它。')) {
      return;
    }
    try {
      await tenderApi.deleteClause(clauseId);
      if (editingClauseId === clauseId) {
        setEditingClauseId(null);
        resetClauseForm(clauseForm.category);
      }
      await loadLibrary();
      setNotice('条款已删除');
    } catch (err) {
      setError(getErrorMessage(err, '删除条款失败'));
    }
  };

  const handleSubmitPrice = async () => {
    if (!priceForm.name.trim()) {
      setError('价格参考名称不能为空');
      return;
    }
    setSavingPrice(true);
    setError(null);
    setNotice(null);
    try {
      const payload = {
        name: priceForm.name.trim(),
        price: Number.parseFloat(priceForm.price) || 0,
        price_type: priceForm.price_type,
        // 传空串而不是 null，便于在编辑时「清空」这些字段
        category: priceForm.category.trim(),
        region: priceForm.region.trim(),
        unit: priceForm.unit.trim(),
        reference_date: priceForm.reference_date.trim(),
      };
      if (editingPriceId !== null) {
        await tenderApi.updatePriceReference(editingPriceId, payload);
        setNotice('价格参考已更新');
      } else {
        await tenderApi.createPriceReference(payload);
        setNotice('价格参考已加入资料库，编制价格相关章节时会作为参照');
      }
      setPriceForm({ ...priceForm, name: '', price: '', unit: '' });
      setEditingPriceId(null);
      await loadLibrary();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          editingPriceId !== null ? '更新价格参考失败' : '新增价格参考失败',
        ),
      );
    } finally {
      setSavingPrice(false);
    }
  };

  /** 载入某条自有价格参考到表单进行编辑（内置条目只读，不提供编辑入口） */
  const handleEditPrice = (item: TenderPriceReference) => {
    setEditingPriceId(item.id);
    setPriceForm({
      name: item.name,
      category: item.category || '',
      region: item.region || '',
      unit: item.unit || '',
      price: item.price === null || item.price === undefined ? '' : String(item.price),
      price_type: item.price_type,
      reference_date: item.reference_date || '',
    });
    setError(null);
    setNotice('正在编辑价格参考，点「保存修改」生效，或点「取消编辑」放弃');
  };

  const handleCancelPriceEdit = () => {
    setEditingPriceId(null);
    setPriceForm({ ...priceForm, name: '', price: '', unit: '' });
  };

  const handleDeletePrice = async (referenceId: number) => {
    if (!window.confirm('确定删除该价格参考吗？删除后编制价格章节将不再参照它。')) {
      return;
    }
    try {
      await tenderApi.deletePriceReference(referenceId);
      if (editingPriceId === referenceId) {
        setEditingPriceId(null);
        setPriceForm({ ...priceForm, name: '', price: '', unit: '' });
      }
      await loadLibrary();
      setNotice('价格参考已删除');
    } catch (err) {
      setError(getErrorMessage(err, '删除价格参考失败'));
    }
  };

  const loadCompliance = useCallback(async (projectId: number) => {
    try {
      const [reportRes, rulesRes] = await Promise.all([
        tenderApi.getComplianceReport(projectId),
        tenderApi.listComplianceRules(),
      ]);
      setComplianceReport(reportRes.data.data ?? null);
      setComplianceRules(rulesRes.data.items || []);
    } catch (err) {
      setError(getErrorMessage(err, '加载合规审核数据失败'));
    }
  }, []);

  useEffect(() => {
    const projectId = detail?.project.id;
    if (tab !== 'compliance' || !projectId) {
      return;
    }
    void loadCompliance(projectId);
  }, [tab, detail?.project.id, loadCompliance]);

  const handleRunCompliance = async () => {
    if (!detail) {
      return;
    }
    setComplianceRunning(true);
    setError(null);
    setNotice(null);
    setComplianceLogs(['开始合规审核（规则检查 → 语义检查）…']);
    try {
      await tenderApi.complianceCheckStream(detail.project.id, (event) => {
        if (event.error) {
          throw new Error(event.message || '合规审核失败');
        }
        if (event.type === 'progress' && event.message) {
          setComplianceLogs((prev) => [...prev, event.message as string]);
        }
        if (event.type === 'result' && event.report) {
          const report = event.report;
          setComplianceReport(report);
          setComplianceLogs((prev) => [
            ...prev,
            `审核完成：${COMPLIANCE_OVERALL_LABELS[report.status]}（通过 ${report.passed_count} / 需修改 ${report.warning_count} / 风险 ${report.risk_count}）`,
          ]);
        }
      });
    } catch (err) {
      setError(getErrorMessage(err, '合规审核失败，请稍后重试'));
      setComplianceLogs((prev) => [...prev, getErrorMessage(err, '合规审核失败')]);
    } finally {
      setComplianceRunning(false);
    }
  };

  const handleSubmitRule = async () => {
    if (!ruleForm.title.trim() || !ruleForm.check_prompt.trim()) {
      setError('检查项标题与检查要求不能为空');
      return;
    }
    setSavingRule(true);
    setError(null);
    try {
      const payload = {
        title: ruleForm.title.trim(),
        check_prompt: ruleForm.check_prompt.trim(),
        severity: ruleForm.severity,
      };
      if (editingRuleId !== null) {
        await tenderApi.updateComplianceRule(editingRuleId, payload);
        setNotice('检查项已更新，重新执行合规审核时生效');
      } else {
        await tenderApi.createComplianceRule(payload);
        setNotice('检查项已加入，重新执行合规审核时生效');
      }
      setRuleForm({ title: '', check_prompt: '', severity: ruleForm.severity });
      setEditingRuleId(null);
      if (detail) {
        await loadCompliance(detail.project.id);
      }
    } catch (err) {
      setError(
        getErrorMessage(err, editingRuleId !== null ? '更新检查项失败' : '新增检查项失败'),
      );
    } finally {
      setSavingRule(false);
    }
  };

  /** 载入某条自定义检查项到表单进行编辑（内置检查项只读，不提供编辑入口） */
  const handleEditRule = (rule: TenderComplianceRule) => {
    setEditingRuleId(rule.id);
    setRuleForm({
      title: rule.title,
      check_prompt: rule.check_prompt,
      severity: rule.severity,
    });
    setError(null);
    setNotice('正在编辑检查项，点「保存修改」生效，或点「取消编辑」放弃');
  };

  const handleCancelRuleEdit = () => {
    setEditingRuleId(null);
    setRuleForm({ title: '', check_prompt: '', severity: ruleForm.severity });
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!window.confirm('确定删除该检查项吗？删除后合规审核将不再核查它。')) {
      return;
    }
    try {
      await tenderApi.deleteComplianceRule(ruleId);
      if (editingRuleId === ruleId) {
        setEditingRuleId(null);
        setRuleForm({ title: '', check_prompt: '', severity: ruleForm.severity });
      }
      if (detail) {
        await loadCompliance(detail.project.id);
      }
      setNotice('检查项已删除');
    } catch (err) {
      setError(getErrorMessage(err, '删除检查项失败'));
    }
  };

  const buildOutlineTree = useCallback((): OutlineItem[] => {
    if (!detail) {
      return [];
    }
    const childrenOf = new Map<string | null, TenderOutlineNode[]>();
    detail.outline.forEach((node) => {
      const key = node.parent_node_id ?? null;
      childrenOf.set(key, [...(childrenOf.get(key) || []), node]);
    });
    const build = (parentNodeId: string | null): OutlineItem[] =>
      (childrenOf.get(parentNodeId) || [])
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((node) => ({
          id: node.node_id,
          stable_id: node.stable_id,
          title: node.title,
          description: node.description || '',
          word_count_range: node.word_count_range || undefined,
          section: node.section,
          content: contents[node.stable_id] || '',
          children: build(node.node_id),
        }));
    return build(null);
  }, [detail, contents]);

  /** 下载导出文件：必须带 Bearer token（导出下载接口需要认证），不能用 <a href> 直接打开 */
  const handleDownloadExport = async () => {
    if (!exportTaskId) {
      return;
    }
    setExportError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(documentApi.getExportDownloadUrl(exportTaskId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        throw new Error(`下载失败（HTTP ${res.status}）`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFileName || `${detail?.project.name || '招标文件'}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setNotice('导出文件已开始下载');
    } catch (err) {
      setExportError(getErrorMessage(err, '下载导出文件失败'));
    }
  };

  const handleExport = async () => {
    if (!hasFeature(license?.features, 'export_word')) {
      setError('未授权「Word 导出」功能，请联系管理员');
      return;
    }
    if (!detail) {
      return;
    }
    const outline = buildOutlineTree();
    if (outline.length === 0) {
      setExportError('目录为空，无法导出');
      return;
    }
    setExporting(true);
    setExportError(null);
    setExportStatus('pending');
    try {
      const res = await documentApi.exportWordBatch({
        project_name: detail.project.name,
        outline,
        template_id: exportTemplateId || undefined,
        generation_mode: 'full',
        cover_title: '招标文件',
      });
      setExportTaskId(res.data.task_id);
    } catch (err) {
      setExporting(false);
      setExportError(getErrorMessage(err, '提交导出任务失败'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition hover:bg-gray-50"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              切换模块
            </button>
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                <DocumentTextIcon className="h-5 w-5" />
              </span>
              <div>
                <h1 className="text-base font-semibold text-gray-900">招标文件编写</h1>
                <p className="text-xs text-gray-500">
                  选模板 → 填立项信息 → 逐章编制 → 导出（招标方视角）
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {detail && detail.is_owner && (
              <>
                <button
                  type="button"
                  onClick={() => setShowVersions(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition hover:bg-gray-50"
                  title="保存/恢复版本、版本对比"
                >
                  <ClockIcon className="h-4 w-4" />
                  版本
                </button>
                <button
                  type="button"
                  onClick={() => setShowShare(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition hover:bg-gray-50"
                  title="把该项目共享给其他用户协作编辑"
                >
                  <UserGroupIcon className="h-4 w-4" />
                  共享
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onLogout}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 transition hover:bg-gray-50"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              退出登录
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-4 px-6 py-6 lg:grid-cols-[260px_1fr]">
        {/* 项目列表 */}
        <aside className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-500">我的招标文件项目</p>
            <button
              type="button"
              onClick={() => {
                setShowCreate(true);
                setTab('info');
                setActiveId(null);
              }}
              className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              新建
            </button>
          </div>
          <div className="space-y-1">
            {projects.map((project) => (
              <div
                key={project.id}
                className={`flex items-center gap-1 rounded-lg border transition ${
                  activeId === project.id && !showCreate
                    ? 'border-indigo-300 bg-indigo-50'
                    : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    setActiveId(project.id);
                    setTab('info');
                    setNotice(null);
                    setError(null);
                  }}
                  className={`min-w-0 flex-1 rounded-lg px-3 py-2 text-left text-sm ${
                    activeId === project.id && !showCreate
                      ? 'text-indigo-800'
                      : 'text-gray-700'
                  }`}
                >
                  <span className="block truncate">
                    {project.name}
                    {project.shared && (
                      <span className="ml-1 rounded-full border border-indigo-200 bg-indigo-50 px-1.5 text-[10px] text-indigo-700">
                        共享
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-gray-400">
                    {project.updated_at
                      ? fmtDateTimeCST(project.updated_at)
                      : '未更新'}
                  </span>
                </button>
                {!project.shared && (
                  <button
                    type="button"
                    onClick={() => void handleDeleteProject(project)}
                    title="删除项目（移至回收站，可在设置中恢复）"
                    className="mr-1.5 shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-rose-50 hover:text-rose-600"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            {projects.length === 0 && (
              <p className="rounded-lg bg-white px-3 py-4 text-xs text-gray-400">
                还没有招标文件项目，点击「新建」开始
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              const next = !showTrash;
              setShowTrash(next);
              if (next) {
                void loadTrash();
              }
            }}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600 transition hover:bg-gray-50"
          >
            {showTrash ? '收起回收站' : '回收站（已删除项目可恢复）'}
          </button>
          {showTrash && (
            <div className="space-y-1 rounded-lg border border-gray-200 bg-white p-2">
              {trashItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-xs text-gray-700" title={item.name}>
                      {item.name}
                    </p>
                    <p className="text-[11px] text-gray-400">
                      {item.deleted_at
                        ? fmtDateTimeCST(item.deleted_at)
                        : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleRestoreProject(item.id, item.name)}
                    className="shrink-0 text-[11px] text-indigo-600 hover:underline"
                  >
                    恢复
                  </button>
                </div>
              ))}
              {trashItems.length === 0 && (
                <p className="py-2 text-center text-xs text-gray-400">回收站为空</p>
              )}
            </div>
          )}
        </aside>

        {/* 主区域 */}
        <section className="min-w-0 space-y-3">
          {error && (
            <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
          {notice && (
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {notice}
            </div>
          )}

          {showCreate && (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="text-sm font-semibold text-gray-900">新建招标文件项目</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                选择模板并填写立项信息，系统会按模板生成目录；立项信息会作为各章节编制的统一口径。
              </p>
              <div className="mt-3">
                <TenderProjectForm
                  mode="create"
                  form={createForm}
                  onChange={setCreateForm}
                  templates={templates}
                  submitting={creating}
                  onSubmit={() => void handleCreate()}
                  onCancel={() => setShowCreate(false)}
                  onTemplatesChanged={() => void loadTemplates()}
                />
              </div>
            </div>
          )}

          {!showCreate && activeId === null && (
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500">
              请选择左侧项目，或点击「新建」创建招标文件项目
            </div>
          )}

          {!showCreate && activeId !== null && (
            <div className="rounded-xl border border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-semibold text-gray-900">
                    {detail?.project.name || '加载中…'}
                  </h2>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {detail?.info?.template_name ? `模板：${detail.info.template_name}` : '—'}
                    {detail?.info?.evaluation_method
                      ? ` · ${
                          detail.info.evaluation_method === 'lowest_price'
                            ? '经评审的最低投标价法'
                            : '综合评估法'
                        }`
                      : ''}
                    {detail?.outline?.length ? ` · 目录 ${detail.outline.length} 个章节` : ''}
                  </p>
                </div>
                <div className="flex gap-1">
                  {TABS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setTab(item.key)}
                      className={`rounded-lg px-3 py-1.5 text-xs transition ${
                        tab === item.key
                          ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {loading && !detail && (
                <p className="px-4 py-8 text-center text-sm text-gray-500">加载中…</p>
              )}

              {detail && tab === 'info' && (
                <div className="p-4">
                  {detail.info ? (
                    <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                      立项信息已保存
                      {detail.info.updated_at
                        ? `（更新于 ${fmtDateTimeCST(detail.info.updated_at)}）`
                        : ''}
                      {detail.info.template_name
                        ? ` · 所用模板：${detail.info.template_name}`
                        : ''}
                      {' · '}重新生成目录与编制章节都会使用这套口径
                    </p>
                  ) : (
                    <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      本项目还没有保存立项信息：填写后点「保存立项信息」，重新生成目录与章节编制都会使用这套口径。
                    </p>
                  )}
                  <TenderProjectForm
                    mode="edit"
                    form={editForm}
                    onChange={setEditForm}
                    templates={templates}
                    submitting={savingInfo}
                    onSubmit={() => void handleSaveInfo()}
                    onTemplatesChanged={() => void loadTemplates()}
                  />
                </div>
              )}

              {detail && tab === 'content' && (
                <div className="grid gap-3 p-4 lg:grid-cols-[300px_1fr]">
                  <div className="rounded-lg border border-gray-200 p-2">
                    <div className="flex flex-wrap items-center justify-between gap-2 px-1 pb-1.5">
                      <p className="text-xs font-medium text-gray-500">
                        目录（点击章节进行编制）
                      </p>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setShowOutlineEditor(true)}
                          title="编辑目录：增删改章节、调整层级与顺序、修改描述与字数"
                          className="rounded-lg border border-gray-200 p-1 text-gray-500 transition hover:bg-gray-50"
                        >
                          <PencilSquareIcon className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleSyncOutline()}
                          title="按当前模板同步章节标题与描述（不增删章节，保留已生成的正文与人工修改）"
                          className="rounded-lg border border-gray-200 p-1 text-gray-500 transition hover:bg-gray-50"
                        >
                          <ArrowPathIcon className="h-3 w-3" />
                        </button>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowRegenMenu((prev) => !prev)}
                            disabled={regeneratingOutline}
                            title="按模板重新生成目录：补齐缺失章节，或按模板重建（保留已生成正文）"
                            className="flex items-center gap-0.5 rounded-lg border border-gray-200 p-1 text-gray-500 transition hover:bg-gray-50 disabled:opacity-50"
                          >
                            {regeneratingOutline ? (
                              <ArrowPathIcon className="h-3 w-3 animate-spin" />
                            ) : (
                              <ArrowUturnLeftIcon className="h-3 w-3" />
                            )}
                          </button>
                          {showRegenMenu && (
                            <div className="absolute right-0 z-20 mt-1 w-60 rounded-lg border border-gray-200 bg-white p-1 shadow-lg">
                              <button
                                type="button"
                                onClick={() => void handleRegenerateOutline('merge')}
                                className="w-full rounded-md px-2 py-1.5 text-left text-[11px] text-gray-700 transition hover:bg-gray-50"
                              >
                                补齐模板缺失章节（推荐）
                                <span className="mt-0.5 block text-[10px] text-gray-400">
                                  保留现有章节、人工增补章节与已生成正文
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleRegenerateOutline('replace')}
                                className="w-full rounded-md px-2 py-1.5 text-left text-[11px] text-rose-600 transition hover:bg-rose-50"
                              >
                                按模板重建整棵目录
                                <span className="mt-0.5 block text-[10px] text-gray-400">
                                  移除模板外章节，正文按标题匹配保留
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                        {batchRunning ? (
                          <button
                            type="button"
                            onClick={handleStopBatch}
                            className="flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-700 transition hover:bg-rose-100"
                          >
                            <ArrowPathIcon className="h-3 w-3 animate-spin" />
                            停止
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => void handleGenerateAll()}
                            disabled={detail.outline.length === 0}
                            title={
                              pendingCount > 0
                                ? `仅编制尚无内容的 ${pendingCount} 个章节，不覆盖已有内容`
                                : '全部章节都已有内容，点击将全部重新编制'
                            }
                            className="flex items-center gap-1 rounded-lg bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
                          >
                            <SparklesIcon className="h-3 w-3" />
                            {pendingCount > 0
                              ? `编制所有章节（${pendingCount} 章待编制）`
                              : '全部重新编制'}
                          </button>
                        )}
                      </div>
                    </div>
                    {batchRunning && logs.length > 0 && (
                      <p
                        className="truncate px-1 pb-1 text-[11px] text-indigo-600"
                        title={logs[logs.length - 1]}
                      >
                        {logs[logs.length - 1]}
                      </p>
                    )}
                    <div className="max-h-[560px] overflow-y-auto">
                      <TenderOutlinePanel
                        nodes={detail.outline}
                        contents={contents}
                        selectedStableId={selectedStableId}
                        onSelect={(node) => setSelectedStableId(node.stable_id)}
                      />
                    </div>
                  </div>

                  <div className="min-w-0 rounded-lg border border-gray-200 p-3">
                    {!selectedNode && (
                      <p className="py-10 text-center text-sm text-gray-500">
                        请选择左侧章节开始编制
                      </p>
                    )}
                    {selectedNode && (
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {selectedNode.node_id} {selectedNode.title}
                            </p>
                            <p className="mt-0.5 text-xs text-gray-500">
                              {PART_KIND_LABELS[selectedNode.part_kind] || '其他'}
                              {selectedNode.word_count_range
                                ? ` · 建议 ${selectedNode.word_count_range} 字`
                                : ''}
                              {selectedNode.description
                                ? ` · ${selectedNode.description}`
                                : ' · 未填写编写要点（可点目录面板的铅笔图标补充，或点 ⟳ 按模板同步）'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex rounded-lg border border-gray-200 p-0.5">
                              {(
                                [
                                  { key: 'preview' as const, label: '预览' },
                                  { key: 'edit' as const, label: '编辑' },
                                ]
                              ).map((item) => (
                                <button
                                  key={item.key}
                                  type="button"
                                  onClick={() => setContentView(item.key)}
                                  className={`rounded-md px-2.5 py-1 text-[11px] transition ${
                                    contentView === item.key
                                      ? 'bg-indigo-50 text-indigo-700'
                                      : 'text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {item.label}
                                </button>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={() => void handleGenerate(selectedNode)}
                              disabled={generatingId === selectedNode.stable_id}
                              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
                            >
                              {generatingId === selectedNode.stable_id ? (
                                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <DocumentTextIcon className="h-3.5 w-3.5" />
                              )}
                              {contents[selectedNode.stable_id]
                                ? '重新编制本章'
                                : '编制本章'}
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleSaveContent()}
                              disabled={savingContent}
                              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            >
                              {savingContent ? '保存中…' : '保存本章'}
                            </button>
                          </div>
                        </div>

                        {logs.length > 0 && (
                          <div className="max-h-24 overflow-y-auto rounded-lg bg-gray-900/90 px-3 py-2 font-mono text-[11px] leading-relaxed text-gray-100">
                            {logs.map((line, index) => (
                              <p key={`${line}-${index}`}>{line}</p>
                            ))}
                          </div>
                        )}

                        {contentView === 'preview' ? (
                          <div className="min-h-[420px] max-h-[70vh] overflow-y-auto rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm leading-relaxed">
                            {(contents[selectedNode.stable_id] || '').trim() ? (
                              <MarkdownRenderer
                                content={contents[selectedNode.stable_id] || ''}
                                mermaidDefaultView="chart"
                              />
                            ) : (
                              <p className="py-8 text-center text-xs text-gray-400">
                                本章暂无内容：点击「编制本章」生成初稿，或切到「编辑」手工编写
                              </p>
                            )}
                          </div>
                        ) : (
                          <textarea
                            value={contents[selectedNode.stable_id] || ''}
                            onChange={(event) => {
                              const stableId = selectedNode.stable_id;
                              setContents((prev) => ({
                                ...prev,
                                [stableId]: event.target.value,
                              }));
                              setDirtyIds((prev) => new Set(prev).add(stableId));
                            }}
                            rows={20}
                            placeholder="点击「编制本章」生成初稿，或直接在此手工编写（Markdown）"
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:border-indigo-400"
                          />
                        )}
                        <p className="text-[11px] text-gray-400">
                          正文以 Markdown 保存（支持标题、表格、列表、公式与 Mermaid 图表）；
                          {contentView === 'preview'
                            ? '切换「编辑」可修改源码，'
                            : '切换「预览」可查看渲染效果，'}
                          编制完成后记得点击「保存本章」。
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {detail && tab === 'library' && (
                <div className="grid gap-4 p-4 lg:grid-cols-2">
                  {/* 条款库 */}
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-medium text-gray-600">
                        条款库（编制对应分部时自动引用，最多 6 条）
                      </p>
                      <select
                        value={clauseFilter}
                        onChange={(event) =>
                          setClauseFilter(event.target.value as TenderClauseCategory | '')
                        }
                        className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 outline-none focus:border-indigo-400"
                      >
                        <option value="">全部类别</option>
                        {Object.entries(CLAUSE_CATEGORY_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2 rounded-lg border border-gray-200 p-2">
                      {editingClauseId !== null && (
                        <p className="rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                          正在编辑条款 #{editingClauseId}，保存后编制该分部会引用新内容
                        </p>
                      )}
                      <input
                        value={clauseForm.title}
                        onChange={(event) =>
                          setClauseForm({ ...clauseForm, title: event.target.value })
                        }
                        placeholder="条款标题（如 市政总承包资质要求）"
                        className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                      />
                      <select
                        value={clauseForm.category}
                        onChange={(event) =>
                          setClauseForm({
                            ...clauseForm,
                            category: event.target.value as TenderClauseCategory,
                          })
                        }
                        className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 outline-none focus:border-indigo-400"
                      >
                        {Object.entries(CLAUSE_CATEGORY_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <select
                          value={clauseForm.industry}
                          onChange={(event) =>
                            setClauseForm({
                              ...clauseForm,
                              industry: event.target.value as TenderIndustry | '',
                            })
                          }
                          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 outline-none focus:border-indigo-400"
                        >
                          <option value="">适用行业：通用</option>
                          {Object.entries(INDUSTRY_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <input
                          value={clauseForm.legal_basis}
                          onChange={(event) =>
                            setClauseForm({ ...clauseForm, legal_basis: event.target.value })
                          }
                          placeholder="法规/制度依据（选填）"
                          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                        />
                      </div>
                      <div>
                        <p className="mb-1 text-[11px] text-gray-500">
                          适用采购方式（都不选表示通用）
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(PURCHASE_METHOD_LABELS).map(([key, label]) => {
                            const value = key as TenderPurchaseMethod;
                            const checked = clauseForm.purchase_methods.includes(value);
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() =>
                                  setClauseForm({
                                    ...clauseForm,
                                    purchase_methods: checked
                                      ? clauseForm.purchase_methods.filter(
                                          (item) => item !== value,
                                        )
                                      : [...clauseForm.purchase_methods, value],
                                  })
                                }
                                className={`rounded-full border px-2 py-0.5 text-[11px] transition ${
                                  checked
                                    ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                    : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <input
                          value={clauseForm.tagsText}
                          onChange={(event) =>
                            setClauseForm({ ...clauseForm, tagsText: event.target.value })
                          }
                          placeholder="标签（用、或逗号分隔，选填）"
                          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                        />
                        <label className="flex items-center gap-2 text-[11px] text-gray-600">
                          <input
                            type="checkbox"
                            checked={clauseForm.is_active}
                            onChange={(event) =>
                              setClauseForm({ ...clauseForm, is_active: event.target.checked })
                            }
                            className="h-3.5 w-3.5 rounded border-gray-300"
                          />
                          启用（停用后编制章节不再引用）
                        </label>
                      </div>
                      <textarea
                        value={clauseForm.content}
                        onChange={(event) =>
                          setClauseForm({ ...clauseForm, content: event.target.value })
                        }
                        rows={3}
                        placeholder="条款内容（可直接写进招标文件的表述）"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs leading-relaxed outline-none focus:border-indigo-400"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => void handleSubmitClause()}
                          disabled={savingClause}
                          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                        >
                          {savingClause
                            ? '保存中…'
                            : editingClauseId !== null
                              ? '保存修改'
                              : '加入条款库'}
                        </button>
                        {editingClauseId !== null && (
                          <button
                            type="button"
                            onClick={handleCancelClauseEdit}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                          >
                            取消编辑
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-[420px] space-y-1 overflow-y-auto">
                      {clauses.map((clause) => (
                        <div key={clause.id} className="rounded-lg border border-gray-200 p-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800">
                                {clause.title}
                                <span className="ml-1 rounded-full border border-gray-200 bg-gray-50 px-1.5 text-[11px] text-gray-500">
                                  {CLAUSE_CATEGORY_LABELS[clause.category] || clause.category}
                                </span>
                                {!clause.is_active && (
                                  <span className="ml-1 rounded-full border border-gray-200 bg-gray-100 px-1.5 text-[11px] text-gray-500">
                                    已停用
                                  </span>
                                )}
                                {clause.industry && (
                                  <span className="ml-1 text-[11px] text-gray-400">
                                    {INDUSTRY_LABELS[clause.industry] || clause.industry}
                                  </span>
                                )}
                                {clause.purchase_methods.length > 0 && (
                                  <span className="ml-1 text-[11px] text-gray-400">
                                    {clause.purchase_methods
                                      .map((item) => PURCHASE_METHOD_LABELS[item] || item)
                                      .join('、')}
                                  </span>
                                )}
                              </p>
                              <p
                                className="mt-0.5 max-h-12 overflow-hidden whitespace-pre-wrap text-[11px] leading-relaxed text-gray-500"
                                title={clause.content}
                              >
                                {clause.content}
                              </p>
                              {(clause.tags.length > 0 || clause.legal_basis) && (
                                <p className="mt-0.5 text-[11px] text-gray-400">
                                  {clause.tags.length > 0 ? `标签：${clause.tags.join('、')}` : ''}
                                  {clause.tags.length > 0 && clause.legal_basis ? ' · ' : ''}
                                  {clause.legal_basis ? `依据：${clause.legal_basis}` : ''}
                                </p>
                              )}
                            </div>
                            {clause.user_id === null ? (
                              <span className="shrink-0 text-[11px] text-gray-400">内置</span>
                            ) : (
                              <div className="flex shrink-0 items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditClause(clause)}
                                  className="text-[11px] text-indigo-600 hover:underline"
                                >
                                  编辑
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleDeleteClause(clause.id)}
                                  className="text-[11px] text-rose-600 hover:underline"
                                >
                                  删除
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {clauses.length === 0 && (
                        <p className="px-2 py-4 text-center text-xs text-gray-400">
                          条款库为空，先添加常用资格条件或合同条款
                        </p>
                      )}
                    </div>
                  </div>

                  {/* 价格参考库 */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600">
                      价格参考库（用于最高限价与价格条款策划的合理性参照）
                    </p>
                    <div className="grid gap-2 rounded-lg border border-gray-200 p-2 sm:grid-cols-2">
                      <input
                        value={priceForm.name}
                        onChange={(event) =>
                          setPriceForm({ ...priceForm, name: event.target.value })
                        }
                        placeholder="名称（如 沥青混凝土路面）"
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                      />
                      <input
                        value={priceForm.category}
                        onChange={(event) =>
                          setPriceForm({ ...priceForm, category: event.target.value })
                        }
                        list="price-category-options"
                        placeholder="品类（下拉选择）"
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                      />
                      <datalist id="price-category-options">
                        {priceCategoryOptions.map((item) => (
                          <option key={item} value={item} />
                        ))}
                      </datalist>
                      <input
                        value={priceForm.region}
                        onChange={(event) =>
                          setPriceForm({ ...priceForm, region: event.target.value })
                        }
                        placeholder="地区"
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                      />
                      <input
                        value={priceForm.unit}
                        onChange={(event) =>
                          setPriceForm({ ...priceForm, unit: event.target.value })
                        }
                        list="price-unit-options"
                        placeholder="单位（下拉选择）"
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                      />
                      <datalist id="price-unit-options">
                        {priceUnitOptions.map((item) => (
                          <option key={item} value={item} />
                        ))}
                      </datalist>
                      <input
                        value={priceForm.price}
                        onChange={(event) =>
                          setPriceForm({ ...priceForm, price: event.target.value })
                        }
                        placeholder="价格"
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                      />
                      <select
                        value={priceForm.price_type}
                        onChange={(event) =>
                          setPriceForm({
                            ...priceForm,
                            price_type: event.target.value as TenderPriceType,
                          })
                        }
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 outline-none focus:border-indigo-400"
                      >
                        {Object.entries(PRICE_TYPE_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <input
                        value={priceForm.reference_date}
                        onChange={(event) =>
                          setPriceForm({ ...priceForm, reference_date: event.target.value })
                        }
                        placeholder="参考日期（YYYY-MM）"
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400 sm:col-span-2"
                      />
                      {editingPriceId !== null && (
                        <p className="rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-700 sm:col-span-2">
                          正在编辑价格参考 #{editingPriceId}，保存后生效
                        </p>
                      )}
                      <div className="flex items-center gap-2 sm:col-span-2">
                        <button
                          type="button"
                          onClick={() => void handleSubmitPrice()}
                          disabled={savingPrice}
                          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                        >
                          {savingPrice
                            ? '保存中…'
                            : editingPriceId !== null
                              ? '保存修改'
                              : '加入价格库'}
                        </button>
                        {editingPriceId !== null && (
                          <button
                            type="button"
                            onClick={handleCancelPriceEdit}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                          >
                            取消编辑
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-[420px] space-y-1 overflow-y-auto">
                      {priceRefs.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-start justify-between gap-2 rounded-lg border border-gray-200 p-2"
                        >
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-gray-800">
                              {item.name}
                              <span className="ml-1 rounded-full border border-gray-200 bg-gray-50 px-1.5 text-[11px] text-gray-500">
                                {PRICE_TYPE_LABELS[item.price_type] || item.price_type}
                              </span>
                            </p>
                            <p className="mt-0.5 text-[11px] text-gray-500">
                              {item.price} {item.unit || ''}
                              {item.category ? ` · ${item.category}` : ''}
                              {item.region ? ` · ${item.region}` : ''}
                              {item.reference_date ? ` · ${item.reference_date}` : ''}
                            </p>
                          </div>
                          {item.user_id === null ? (
                            <span className="shrink-0 text-[11px] text-gray-400">内置</span>
                          ) : (
                            <div className="flex shrink-0 items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditPrice(item)}
                                className="text-[11px] text-indigo-600 hover:underline"
                              >
                                编辑
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleDeletePrice(item.id)}
                                className="text-[11px] text-rose-600 hover:underline"
                              >
                                删除
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      {priceRefs.length === 0 && (
                        <p className="px-2 py-4 text-center text-xs text-gray-400">
                          价格库为空，先补充同类项目的预算或中标价
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {detail && tab === 'compliance' && (
                <div className="grid gap-4 p-4 lg:grid-cols-[1fr_320px]">
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => void handleRunCompliance()}
                        disabled={complianceRunning}
                        className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
                      >
                        {complianceRunning ? (
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <ShieldCheckIcon className="h-4 w-4" />
                        )}
                        {complianceRunning ? '审核中…' : '执行合规审核'}
                      </button>
                      <span className="text-xs text-gray-500">
                        规则检查（法定时限 / 分部完整性 / 价格分设置 / 限价与预算关系 / 占位与完成度）
                        + 语义检查（{complianceRules.length} 条检查项）
                      </span>
                    </div>

                    {complianceLogs.length > 0 && (
                      <div className="max-h-28 overflow-y-auto rounded-lg bg-gray-900/90 px-3 py-2 font-mono text-[11px] leading-relaxed text-gray-100">
                        {complianceLogs.map((line, index) => (
                          <p key={`${line}-${index}`}>{line}</p>
                        ))}
                      </div>
                    )}

                    {complianceReport ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs">
                          <span
                            className={`rounded-full border px-2 py-0.5 font-medium ${
                              complianceReport.status === 'passed'
                                ? COMPLIANCE_STATUS_CLASSES.pass
                                : complianceReport.status === 'warning'
                                  ? COMPLIANCE_STATUS_CLASSES.warning
                                  : COMPLIANCE_STATUS_CLASSES.risk
                            }`}
                          >
                            {COMPLIANCE_OVERALL_LABELS[complianceReport.status]}
                          </span>
                          <span className="text-gray-600">共 {complianceReport.total} 项</span>
                          <span className="text-emerald-700">
                            通过 {complianceReport.passed_count}
                          </span>
                          <span className="text-amber-700">
                            需修改 {complianceReport.warning_count}
                          </span>
                          <span className="text-rose-700">
                            存在风险 {complianceReport.risk_count}
                          </span>
                          {complianceReport.llm_rule_count === 0 && (
                            <span className="text-gray-400">（未包含语义检查）</span>
                          )}
                          {complianceReport.created_at && (
                            <span className="text-gray-400">
                              {fmtDateTimeCST(complianceReport.created_at)}
                            </span>
                          )}
                        </div>

                        {complianceReport.summary_md && (
                          <p className="whitespace-pre-wrap rounded-lg border border-gray-200 px-3 py-2 text-xs leading-relaxed text-gray-700">
                            {complianceReport.summary_md}
                          </p>
                        )}

                        <div className="space-y-1.5">
                          {complianceReport.items.map((item) => (
                            <div
                              key={`${item.code}-${item.source}`}
                              className="rounded-lg border border-gray-200 p-2.5"
                            >
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span
                                  className={`rounded-full border px-1.5 text-[11px] ${
                                    COMPLIANCE_STATUS_CLASSES[item.status]
                                  }`}
                                >
                                  {COMPLIANCE_STATUS_LABELS[item.status]}
                                </span>
                                <span className="text-xs font-medium text-gray-800">
                                  {item.title}
                                </span>
                                <span className="rounded-full border border-gray-200 bg-gray-50 px-1.5 text-[11px] text-gray-500">
                                  {COMPLIANCE_CATEGORY_LABELS[
                                    item.category as keyof typeof COMPLIANCE_CATEGORY_LABELS
                                  ] || item.category}
                                </span>
                                <span className="text-[11px] text-gray-400">
                                  {COMPLIANCE_SEVERITY_LABELS[item.severity] || item.severity} ·{' '}
                                  {item.source === 'rule' ? '规则' : '模型'}
                                </span>
                              </div>
                              {item.finding && (
                                <p className="mt-1 text-xs leading-relaxed text-gray-700">
                                  {item.finding}
                                </p>
                              )}
                              {item.suggestion && (
                                <p className="mt-0.5 text-xs leading-relaxed text-indigo-700">
                                  建议：{item.suggestion}
                                </p>
                              )}
                              {item.legal_basis && (
                                <p className="mt-0.5 text-[11px] text-gray-400">
                                  依据：{item.legal_basis}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>

                        <p className="text-[11px] text-gray-400">
                          以上为编制辅助检查结果，不构成法律意见；最终以法务与主管部门审核为准。
                        </p>
                      </div>
                    ) : (
                      <p className="rounded-lg border border-gray-200 bg-white px-3 py-6 text-center text-xs text-gray-500">
                        尚未执行合规审核
                      </p>
                    )}
                  </div>

                  {/* 检查项管理 */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-600">
                      检查项（交给模型逐项核查，可自定义）
                    </p>
                    <div className="space-y-2 rounded-lg border border-gray-200 p-2">
                      {editingRuleId !== null && (
                        <p className="rounded-md bg-amber-50 px-2 py-1 text-[11px] text-amber-700">
                          正在编辑检查项 #{editingRuleId}，保存后需重新执行合规审核才生效
                        </p>
                      )}
                      <input
                        value={ruleForm.title}
                        onChange={(event) =>
                          setRuleForm({ ...ruleForm, title: event.target.value })
                        }
                        placeholder="检查项标题（如 是否要求本地业绩）"
                        className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-400"
                      />
                      <select
                        value={ruleForm.severity}
                        onChange={(event) =>
                          setRuleForm({
                            ...ruleForm,
                            severity: event.target.value as TenderComplianceSeverity,
                          })
                        }
                        className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 outline-none focus:border-indigo-400"
                      >
                        {Object.entries(COMPLIANCE_SEVERITY_LABELS).map(([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <textarea
                        value={ruleForm.check_prompt}
                        onChange={(event) =>
                          setRuleForm({ ...ruleForm, check_prompt: event.target.value })
                        }
                        rows={3}
                        placeholder="检查要求（写清要让模型核查什么、判定标准）"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs leading-relaxed outline-none focus:border-indigo-400"
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => void handleSubmitRule()}
                          disabled={savingRule}
                          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
                        >
                          {savingRule
                            ? '保存中…'
                            : editingRuleId !== null
                              ? '保存修改'
                              : '新增检查项'}
                        </button>
                        {editingRuleId !== null && (
                          <button
                            type="button"
                            onClick={handleCancelRuleEdit}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
                          >
                            取消编辑
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-[360px] space-y-1 overflow-y-auto">
                      {complianceRules.map((rule) => (
                        <div key={rule.id} className="rounded-lg border border-gray-200 p-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800">{rule.title}</p>
                              <p className="mt-0.5 max-h-10 overflow-hidden text-[11px] leading-relaxed text-gray-500">
                                {rule.check_prompt}
                              </p>
                              <p className="mt-0.5 text-[11px] text-gray-400">
                                {COMPLIANCE_SEVERITY_LABELS[rule.severity] || rule.severity} ·{' '}
                                {rule.user_id === null ? '内置' : '自定义'}
                              </p>
                            </div>
                            {rule.user_id !== null && (
                              <div className="flex shrink-0 items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditRule(rule)}
                                  className="text-[11px] text-indigo-600 hover:underline"
                                >
                                  编辑
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleDeleteRule(rule.id)}
                                  className="text-[11px] text-rose-600 hover:underline"
                                >
                                  删除
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {complianceRules.length === 0 && (
                        <p className="px-2 py-4 text-center text-xs text-gray-400">
                          暂无检查项
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {detail && tab === 'export' && (
                <div className="space-y-3 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-gray-500">Word 版式模板</span>
                    <select
                      value={exportTemplateId}
                      onChange={(event) => setExportTemplateId(event.target.value)}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 outline-none focus:border-indigo-400"
                    >
                      {exportTemplates.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => void handleExport()}
                      disabled={exporting}
                      className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-medium text-white transition hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {exporting ? (
                        <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      )}
                      导出 Word（全套）
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    导出包含全部章节（已保存内容为准，未保存的编辑请先在本页保存）。
                  </p>
                  {exportStatus && (
                    <div className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-700">
                      导出状态：{exportStatus}
                      {exportStatus === 'completed' && exportTaskId && (
                        <button
                          type="button"
                          onClick={() => void handleDownloadExport()}
                          className="ml-2 text-indigo-600 hover:underline"
                        >
                          下载文件
                        </button>
                      )}
                    </div>
                  )}
                  {exportError && (
                    <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                      {exportError}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      {detail && (
        <>
          <VersionManager
            projectId={detail.project.id}
            isOpen={showVersions}
            onClose={() => setShowVersions(false)}
            onRestore={() => {
              setShowVersions(false);
              void loadDetail(detail.project.id);
            }}
            currentChapters={
              Object.values(contents).filter((item) => (item || '').trim()).length
            }
            currentChapterContents={Object.fromEntries(
              detail.outline.map((node) => [node.node_id, contents[node.stable_id] || '']),
            )}
            currentOutlineNodes={detail.outline.map((node) => ({
              node_id: node.node_id,
              parent_node_id: node.parent_node_id,
              title: node.title,
              description: node.description || '',
              word_count_range: node.word_count_range,
            }))}
          />
          <ShareDialog
            isOpen={showShare}
            onClose={() => setShowShare(false)}
            entityType="project"
            entityId={detail.project.id}
            entityName={detail.project.name}
            isOwner
          />
          <TenderOutlineEditor
            open={showOutlineEditor}
            nodes={detail.outline}
            filledStableIds={filledStableIds}
            saving={savingOutline}
            onClose={() => setShowOutlineEditor(false)}
            onSave={(payload) => void handleSaveOutline(payload)}
          />
        </>
      )}
    </div>
  );
}
